import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Volume2, Mic, Brain, Eye, Compass, GraduationCap, Stethoscope, Users, 
  ClipboardList, Layers, Activity, CreditCard, MessageCircle, Box, TrendingUp, 
  Bot, Sun, Video, Baby, Briefcase, Waves, Music, Heart, Cpu, BarChart2, 
  Zap, ClipboardCheck, MapPin, Gamepad2, Play, Download, Lock, CheckCircle2,
  Sparkles, Monitor, Smartphone, ShieldCheck, UserCheck, Grid, Headphones
} from 'lucide-react';
import { AACModule } from './modules/AACModule';
import { SpeechTherapyModule } from './modules/SpeechTherapyModule';
import { HearingScreenerModule } from './modules/HearingScreenerModule';
import { AutismABAModule } from './modules/AutismABAModule';
import { SignLanguageModule } from './modules/SignLanguageModule';
import { BrailleModule } from './modules/BrailleModule';
import { StudentIEPModule } from './modules/StudentIEPModule';
import { TherapistSOAPModule } from './modules/TherapistSOAPModule';
import { TeacherClassroomModule } from './modules/TeacherClassroomModule';
import { AssessmentModule } from './modules/AssessmentModule';
import { SchoolERPModule } from './modules/SchoolERPModule';
import { HospitalRehabModule } from './modules/HospitalRehabModule';
import { NDISBillingModule } from './modules/NDISBillingModule';
import { ParentCommModule } from './modules/ParentCommModule';
import { InventoryModule } from './modules/InventoryModule';
import { AnalyticsBIModule } from './modules/AnalyticsBIModule';
import { AICopilotModule } from './modules/AICopilotModule';
import { SensoryRoomModule } from './modules/SensoryRoomModule';
import { TeleRehabModule } from './modules/TeleRehabModule';
import { EarlyInterventionModule } from './modules/EarlyInterventionModule';
import { VocationalLivingModule } from './modules/VocationalLivingModule';
import { HydrotherapyModule } from './modules/HydrotherapyModule';
import { MusicTherapyModule } from './modules/MusicTherapyModule';
import { AnimalTherapyModule } from './modules/AnimalTherapyModule';
import { ProstheticsModule } from './modules/ProstheticsModule';
import { VBMAPPModule } from './modules/VBMAPPModule';
import { CoreVocabAIModule } from './modules/CoreVocabAIModule';
import { FIMScoringModule } from './modules/FIMScoringModule';
import { EVVGPSModule } from './modules/EVVGPSModule';
import { GamifiedNeuroModule } from './modules/GamifiedNeuroModule';
import { TransitionMatrixModule } from './modules/TransitionMatrixModule';
import { UserSession } from '../AppShell';
import { DEMO_STUDENTS, DemoStudent, getAccessibleStudents } from '../data/demoStudents';
import { TreatmentAuditTracker } from './TreatmentAuditTracker';
import { DISABILITY_CLUSTERS, getDisabilityForStudent, DisabilityCategory } from '../data/disabilityTaxonomy';

interface ModuleExplorerProps {
  initialModule?: string;
  currentUser?: UserSession | null;
}

export const ModuleExplorer: React.FC<ModuleExplorerProps> = ({ initialModule = 'aac', currentUser }) => {
  const { t } = useTranslation();
  const [activeModule, setActiveModule] = useState(initialModule);
  const [explorerTab, setExplorerTab] = useState<'modules' | 'audit_tracker'>('modules');
  
  const accessibleStudents = getAccessibleStudents(currentUser);
  const [selectedStudentId, setSelectedStudentId] = useState(accessibleStudents[0]?.id || DEMO_STUDENTS[0].id);
  const [downloadSuccess, setDownloadSuccess] = useState('');

  const activeStudent: DemoStudent = accessibleStudents.find(s => s.id === selectedStudentId) || accessibleStudents[0] || DEMO_STUDENTS[0];
  const isDirectStudentLogin = currentUser?.role === 'STUDENT';

  // Disability-Centric Sorting State
  const [filterMode, setFilterMode] = useState<'disability' | 'all'>('disability');
  const initialDisability = getDisabilityForStudent(activeStudent.disabilityCategory);
  const [selectedDisabilityId, setSelectedDisabilityId] = useState<string>(initialDisability.id);

  // Keep selectedStudentId valid when currentUser changes
  useEffect(() => {
    const freshAccessible = getAccessibleStudents(currentUser);
    if (!freshAccessible.some(s => s.id === selectedStudentId)) {
      setSelectedStudentId(freshAccessible[0]?.id || DEMO_STUDENTS[0].id);
    }
  }, [currentUser]);

  // Update selected disability when student changes
  useEffect(() => {
    const studentCategory = getDisabilityForStudent(activeStudent.disabilityCategory);
    setSelectedDisabilityId(studentCategory.id);
  }, [activeStudent.id]);

  const modules = [
    { id: 'aac', label: t('mod_aac'), filePrefix: 'AAC_SpeechSynthesizer', icon: Volume2, category: 'Assistive Communication' },
    { id: 'speech-therapy', label: t('mod_speech'), filePrefix: 'SpeechTherapy_Articulation', icon: Mic, category: 'Clinical Therapy' },
    { id: 'autism', label: t('mod_autism'), filePrefix: 'Autism_ABA_Behavior', icon: Brain, category: 'Behavioral Health' },
    { id: 'sign-language', label: t('mod_sign'), filePrefix: 'SignLanguage_Gestures', icon: Eye, category: 'Visual / Hearing' },
    { id: 'braille', label: t('mod_braille'), filePrefix: 'Braille_Tactile_Learning', icon: Compass, category: 'Visual Impairment' },
    { id: 'student', label: t('mod_student'), filePrefix: 'Student_IEP_Progress', icon: GraduationCap, category: 'Special Education' },
    { id: 'therapist', label: t('mod_therapist'), filePrefix: 'Therapist_SOAP_Notes', icon: Stethoscope, category: 'Clinical Operations' },
    { id: 'teacher', label: t('mod_teacher'), filePrefix: 'Teacher_Classroom_Lesson', icon: Users, category: 'School Education' },
    { id: 'assessment', label: t('mod_assessment'), filePrefix: 'Diagnostic_Clinical_Assessment', icon: ClipboardList, category: 'Evaluations' },
    { id: 'school-erp', label: t('mod_erp'), filePrefix: 'SpecialSchool_ERP_Timetable', icon: Layers, category: 'Administration' },
    { id: 'hospital', label: t('mod_hospital'), filePrefix: 'RehabHospital_PatientCare', icon: Activity, category: 'Inpatient Care' },
    { id: 'billing', label: t('mod_billing'), filePrefix: 'NDIS_Clinical_Billing', icon: CreditCard, category: 'Finance' },
    { id: 'communication', label: t('mod_comm'), filePrefix: 'ParentComm_Messenger', icon: MessageCircle, category: 'Family Engagement' },
    { id: 'inventory', label: t('mod_inventory'), filePrefix: 'AssistiveDevice_Inventory', icon: Box, category: 'Resources' },
    { id: 'analytics', label: t('mod_analytics'), filePrefix: 'ExecutiveAnalytics_BI', icon: TrendingUp, category: 'Reporting' },
    { id: 'ai', label: t('mod_ai'), filePrefix: 'AICopilot_ReportSummarizer', icon: Bot, category: 'Intelligence' },
    { id: 'sensory-room', label: t('mod_sensory'), filePrefix: 'SensoryRoom_Snoezelen_Controller', icon: Sun, category: 'Environmental' },
    { id: 'tele-rehab', label: t('mod_tele'), filePrefix: 'TeleRehab_RemoteVideo_Therapy', icon: Video, category: 'Tele-Health' },
    { id: 'early-intervention', label: t('mod_early'), filePrefix: 'EarlyIntervention_Developmental_Tracker', icon: Baby, category: 'Pediatrics' },
    { id: 'vocational-living', label: t('mod_vocal'), filePrefix: 'Vocational_IndependentLiving_Skills', icon: Briefcase, category: 'Adult Rehab' },
    { id: 'hydrotherapy', label: t('mod_hydro'), filePrefix: 'Hydrotherapy_AquaticRehab_Controller', icon: Waves, category: 'Physical Rehab' },
    { id: 'music-therapy', label: t('mod_music'), filePrefix: 'MusicTherapy_RhythmicAuditory_Stimulation', icon: Music, category: 'Neuro-Rehab' },
    { id: 'animal-therapy', label: t('mod_animal'), filePrefix: 'AnimalAssisted_Therapy_PetCare', icon: Heart, category: 'Emotional' },
    { id: 'prosthetics', label: t('mod_prosthetics'), filePrefix: 'Prosthetics_Orthotics_Exoskeleton_Fitting', icon: Cpu, category: 'Bionics' },
    { id: 'vbmapp-assessment', label: t('mod_vbmapp'), filePrefix: 'VBMAPP_Behavioral_Assessment_Protocol', icon: BarChart2, category: 'Behavioral Health' },
    { id: 'core-vocab-ai', label: t('mod_core_vocab'), filePrefix: 'CoreVocab_MotorPlanning_AI_Predictor', icon: Zap, category: 'AAC AI' },
    { id: 'fim-weefim', label: t('mod_fim'), filePrefix: 'FIM_WeeFIM_Clinical_Scoring', icon: ClipboardCheck, category: 'Clinical Rehab' },
    { id: 'evv-gps-tracker', label: t('mod_evv'), filePrefix: 'EVV_GPS_Caregiver_CheckIn', icon: MapPin, category: 'Compliance' },
    { id: 'gamified-neuro', label: t('mod_neuro'), filePrefix: 'Gamified_WebGL_Neuro_Rehab', icon: Gamepad2, category: 'Gamified Rehab' },
    { id: 'transition-matrix', label: t('mod_transition'), filePrefix: 'Secondary_Transition_Vocational_Matrix', icon: Compass, category: 'Post-Secondary' },
    { id: 'audiometry-hearing', label: t('mod_audiometry'), filePrefix: 'Hearing_Audiometry_Screener', icon: Headphones, category: 'Audiology' }
  ];

  // Sync activeModule whenever initialModule prop changes
  useEffect(() => {
    if (initialModule && modules.some(m => m.id === initialModule)) {
      setActiveModule(initialModule);
    }
  }, [initialModule]);

  const isModuleEnabledForTenant = (moduleId: string) => {
    return true; // All 30 modules are 100% enabled for demo & production
  };

  const currentMod = modules.find((m) => m.id === activeModule) || modules[0];

  const handleDownloadApp = (filename: string, name: string) => {
    setDownloadSuccess(`Downloading ${name}... check your downloads folder!`);
    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => setDownloadSuccess(''), 4000);
  };

  const renderActiveModuleComponent = () => {
    switch (activeModule) {
      case 'aac': return <AACModule />;
      case 'speech-therapy': return <SpeechTherapyModule />;
      case 'autism': return <AutismABAModule />;
      case 'sign-language': return <SignLanguageModule />;
      case 'braille': return <BrailleModule />;
      case 'student': return <StudentIEPModule />;
      case 'therapist': return <TherapistSOAPModule />;
      case 'teacher': return <TeacherClassroomModule />;
      case 'assessment': return <AssessmentModule />;
      case 'school-erp': return <SchoolERPModule />;
      case 'hospital': return <HospitalRehabModule />;
      case 'billing': return <NDISBillingModule />;
      case 'communication': return <ParentCommModule />;
      case 'inventory': return <InventoryModule />;
      case 'analytics': return <AnalyticsBIModule />;
      case 'ai': return <AICopilotModule />;
      case 'sensory-room': return <SensoryRoomModule />;
      case 'tele-rehab': return <TeleRehabModule />;
      case 'early-intervention': return <EarlyInterventionModule />;
      case 'vocational-living': return <VocationalLivingModule />;
      case 'hydrotherapy': return <HydrotherapyModule />;
      case 'music-therapy': return <MusicTherapyModule />;
      case 'animal-therapy': return <AnimalTherapyModule />;
      case 'prosthetics': return <ProstheticsModule />;
      case 'vbmapp-assessment': return <VBMAPPModule />;
      case 'core-vocab-ai': return <CoreVocabAIModule />;
      case 'fim-weefim': return <FIMScoringModule />;
      case 'evv-gps-tracker': return <EVVGPSModule />;
      case 'gamified-neuro': return <GamifiedNeuroModule />;
      case 'transition-matrix': return <TransitionMatrixModule />;
      case 'audiometry-hearing': return <HearingScreenerModule />;
      default: return <AACModule />;
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-6 px-4">
      
      {/* Student-First Global Context Selection Banner */}
      <div className="bg-[#1c1b1f] text-white p-5 rounded-[28px] border border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3.5">
          <div className="p-3 bg-[#ffd64e] text-black rounded-full font-black text-sm">
            ☼
          </div>
          <div>
            <div className="text-xs font-bold text-white flex items-center gap-2">
              <span className="text-white/60">{isDirectStudentLogin ? 'Student Session Active:' : 'Active Student Profile Selected:'}</span>
              <span className="text-[#ffd64e] font-extrabold">{activeStudent.name} ({activeStudent.rollId})</span>
            </div>
            <div className="text-[11px] text-white/60 mt-0.5">
              Disability Category: <span className="font-bold text-white">{activeStudent.disabilityCategory}</span> • IEP Target: <span className="font-bold text-[#bfecc7]">{activeStudent.iepGoal}</span>
            </div>
          </div>
        </div>

        {/* Change Student Dropdown (ONLY visible for Admin / Therapist roles; HIDDEN for Student Logins) */}
        {!isDirectStudentLogin ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-white/60 font-bold hidden sm:inline">
              {currentUser?.role === 'SUPER_ADMIN' ? 'Change Student (All Tenants):' : `School Students (${currentUser?.tenant || 'School'}):`}
            </span>
            <select 
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-full font-bold text-xs text-white focus:outline-none focus:border-[#ffd64e] cursor-pointer"
            >
              {accessibleStudents.map(st => (
                <option key={st.id} value={st.id} className="bg-[#1c1b1f] text-white">{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="px-4 py-1.5 bg-[#bfecc7]/20 text-[#bfecc7] border border-[#bfecc7]/30 rounded-full text-xs font-extrabold flex items-center gap-1.5 shrink-0">
            <ShieldCheck className="w-4 h-4 text-[#bfecc7]" />
            <span>Authenticated Student Portal</span>
          </div>
        )}
      </div>

      {/* Workspace View Switcher (All 30 Modules vs Treatment Audit Tracker) */}
      <div className="bg-[#1c1b1f]/5 p-2 rounded-full border border-[#1c1b1f]/10 shadow-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExplorerTab('modules')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              explorerTab === 'modules' 
                ? 'bg-[#ffd64e] text-black shadow-md' 
                : 'bg-white text-[#1c1b1f] hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <Grid className="w-4 h-4" />
            <span>Interactive 30-Module Suite</span>
          </button>

          <button
            onClick={() => setExplorerTab('audit_tracker')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              explorerTab === 'audit_tracker' 
                ? 'bg-[#1c1b1f] text-white shadow-md' 
                : 'bg-white text-[#1c1b1f] hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <Activity className="w-4 h-4 text-emerald-500 animate-pulse" />
            <span>Live Clinical Treatment Audit Tracker</span>
          </button>
        </div>
      </div>

      {downloadSuccess && (
        <div className="p-4 bg-[#bfecc7]/30 border border-[#bfecc7] text-[#1c1b1f] rounded-2xl text-xs font-bold flex items-center gap-2 shadow-sm animate-fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-700" />
          {downloadSuccess}
        </div>
      )}

      {/* VIEW 1: INTERACTIVE 30-MODULE SUITE */}
      {explorerTab === 'modules' && (
        <>
          {/* Top 30-Module Selector Grid with Disability-Centric Sorting */}
          <div className="bg-white p-6 rounded-[28px] border border-slate-200 shadow-sm space-y-4">
            
            {/* View Mode Switcher Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-black text-[#1c1b1f] flex items-center gap-2">
                  <span className="text-[#ffd64e] font-black">☼</span>
                  <span>30-Module Clinical Suite & Disability Taxonomy</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Filter modules mapped specifically to diagnoses under RPwD Act 2016 & DSM-5.</p>
              </div>

              <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-full border border-slate-200 self-start sm:self-auto">
                <button
                  onClick={() => setFilterMode('disability')}
                  className={`px-4 py-1.5 rounded-full text-xs font-black transition flex items-center gap-1.5 cursor-pointer ${
                    filterMode === 'disability'
                      ? 'bg-[#1c1b1f] text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Brain className="w-3.5 h-3.5" />
                  <span>By Disability</span>
                </button>
                <button
                  onClick={() => setFilterMode('all')}
                  className={`px-4 py-1.5 rounded-full text-xs font-black transition flex items-center gap-1.5 cursor-pointer ${
                    filterMode === 'all'
                      ? 'bg-[#1c1b1f] text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Grid className="w-3.5 h-3.5" />
                  <span>All 30 Modules</span>
                </button>
              </div>
            </div>

            {/* Disability Cluster Filter Bar (Active in disability mode) */}
            {filterMode === 'disability' && (
              <div className="space-y-3 pt-1">
                
                {/* 8 Disability Condition Pills */}
                <div className="flex flex-wrap items-center gap-2">
                  {DISABILITY_CLUSTERS.map((cluster) => {
                    const ClusterIcon = cluster.icon;
                    const isSelected = selectedDisabilityId === cluster.id;

                    return (
                      <button
                        key={cluster.id}
                        onClick={() => setSelectedDisabilityId(cluster.id)}
                        className={`px-3.5 py-2 rounded-full text-xs font-black transition-all flex items-center gap-1.5 border shadow-sm cursor-pointer ${
                          isSelected
                            ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow-md ring-2 ring-[#ffd64e]/30'
                            : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200'
                        }`}
                      >
                        <ClusterIcon className="w-3.5 h-3.5" />
                        <span>{cluster.shortName}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Active Disability Information & Clinical Accommodations Box */}
                {(() => {
                  const currentCluster = DISABILITY_CLUSTERS.find(c => c.id === selectedDisabilityId) || DISABILITY_CLUSTERS[0];
                  return (
                    <div className="p-4 bg-[#f9f6f0] rounded-2xl border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-black text-[#1c1b1f] text-sm">{currentCluster.name}</span>
                          <span className="px-2.5 py-0.5 bg-[#ffd64e]/30 text-black font-extrabold text-[10px] rounded-full border border-[#ffd64e]">
                            {currentCluster.rpwdAlignment}
                          </span>
                        </div>
                        <p className="text-slate-600 text-[11px] leading-relaxed max-w-3xl">
                          {currentCluster.description}
                        </p>
                      </div>

                      {/* Accommodations Pills */}
                      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                        {currentCluster.accommodations.map((acc, aidx) => (
                          <span key={aidx} className="px-2.5 py-1 bg-white text-slate-800 border border-slate-200 rounded-full text-[10px] font-bold">
                            ✓ {acc}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}

            {/* Modules Grid */}
            {(() => {
              const currentCluster = DISABILITY_CLUSTERS.find(c => c.id === selectedDisabilityId) || DISABILITY_CLUSTERS[0];
              const displayedModules = filterMode === 'all'
                ? modules
                : modules.filter(m => currentCluster.primaryModuleIds.includes(m.id) || currentCluster.secondaryModuleIds.includes(m.id))
                         .sort((a, b) => {
                           const aPrimary = currentCluster.primaryModuleIds.includes(a.id) ? 0 : 1;
                           const bPrimary = currentCluster.primaryModuleIds.includes(b.id) ? 0 : 1;
                           return aPrimary - bPrimary;
                         });

              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[11px] font-bold text-slate-500">
                    <span>
                      {filterMode === 'disability' 
                        ? `Showing ${displayedModules.length} Targeted Modules for ${currentCluster.shortName}:` 
                        : 'Showing All 30 Authorized Modules:'}
                    </span>
                    <span className="text-black font-black px-2.5 py-0.5 bg-[#ffd64e] rounded-full">{displayedModules.length} Modules Available</span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
                    {displayedModules.map((mod) => {
                      const Icon = mod.icon;
                      const isActive = activeModule === mod.id;
                      const isPrimary = filterMode === 'disability' && currentCluster.primaryModuleIds.includes(mod.id);

                      return (
                        <button
                          key={mod.id}
                          onClick={() => setActiveModule(mod.id)}
                          className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between h-24 cursor-pointer relative overflow-hidden ${
                            isActive 
                              ? 'bg-[#1c1b1f] text-white border-[#ffd64e] ring-2 ring-[#ffd64e] shadow-lg' 
                              : isPrimary 
                                ? 'bg-amber-50/70 text-slate-900 border-amber-300 hover:border-amber-400 shadow-sm'
                                : 'bg-slate-50 text-slate-800 border-slate-200 hover:bg-white hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <Icon className={`w-4 h-4 ${isActive ? 'text-[#ffd64e]' : isPrimary ? 'text-amber-600' : 'text-slate-700'}`} />
                            {filterMode === 'disability' && (
                              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${
                                isActive 
                                  ? 'bg-[#ffd64e] text-black font-black' 
                                  : isPrimary 
                                    ? 'bg-amber-400 text-slate-950 font-black' 
                                    : 'bg-slate-200 text-slate-600'
                              }`}>
                                {isPrimary ? 'Primary' : 'Support'}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] font-black leading-tight line-clamp-2 mt-1">
                            {mod.label}
                          </div>
                          <div className={`text-[9px] truncate font-medium ${isActive ? 'text-white/60' : 'text-slate-500'}`}>
                            {mod.category}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          </div>

          {/* Main Active Module Container */}
          <div className="bg-white rounded-[28px] border border-slate-200 shadow-xl p-6 md:p-8 space-y-6">
            
            {/* Active Module Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
              <div>
                <span className="px-3 py-1 bg-[#1c1b1f] text-[#ffd64e] text-[10px] font-black rounded-full uppercase tracking-wider">
                  {currentMod.category}
                </span>
                <h2 className="text-2xl md:text-3xl font-black text-[#1c1b1f] mt-1.5 flex items-center gap-2">
                  {currentMod.label}
                </h2>
                <p className="text-xs text-slate-500 mt-1">Active Patient Profile: <strong className="text-slate-900">{activeStudent.name}</strong> ({activeStudent.rollId})</p>
              </div>

              {/* Standalone Installers Bar */}
              <div className="flex items-center gap-2 bg-[#f9f6f0] p-2 rounded-full border border-slate-200 text-xs">
                <span className="font-bold text-slate-700 hidden sm:inline ml-2">Install Apps:</span>
                <button
                  onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_Windows_Setup.exe`, `${currentMod.label} Windows App`)}
                  className="px-3.5 py-1.5 bg-[#1c1b1f] hover:bg-black text-white font-bold text-[11px] rounded-full transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <Monitor className="w-3.5 h-3.5 text-[#ffd64e]" /> Windows
                </button>
                <button
                  onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_macOS.dmg`, `${currentMod.label} macOS App`)}
                  className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-[11px] rounded-full transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5 text-[#ffd64e]" /> macOS
                </button>
                <button
                  onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_Android.apk`, `${currentMod.label} Android App`)}
                  className="px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-[11px] rounded-full transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <Smartphone className="w-3.5 h-3.5 text-[#bfecc7]" /> Android
                </button>
              </div>
            </div>

            {/* Active Module Renderer */}
            <div>
              {renderActiveModuleComponent()}
            </div>

          </div>
        </>
      )}

      {/* VIEW 2: LIVE CLINICAL TREATMENT AUDIT TRACKER */}
      {explorerTab === 'audit_tracker' && (
        <TreatmentAuditTracker />
      )}

    </div>
  );
};
